import {AbstractParameter} from 'parameters/AbstractParameter.js';
import {setPulledEffects} from 'actions/index.js';
import {MIDI_FX} from 'constants/effect-types.js';
import AbstractEffect from 'effects/AbstractEffect.js';
import StepData from 'pattern/StepData.js';
import MidiTrigger from './MidiTrigger.js';
import LockableEffect from "effects/LockableEffect.js";

/**
 * Midi effects like "Arp" should be sub classes of this
 * in theory, should be able to link a series of midi effects
 * and have that effect the ultimate set of notes that are 
 * triggered by the sequencer
 */

class MidiEffect extends LockableEffect(AbstractEffect) {
    constructor(name, track) {
        super(name, track);
        this.cacheSize = 16;
        this.initCache();
        this.triggers = [];

        this.numberOfOutputs = 1;
    }
    
    registerContext(context, parentDevice) {
        if (this.context) {
            this.context.combineWith(context);
        } else {
            this.context = context.getCopy();
        }
        if (parentDevice) {
            this.context.forSubDevice(this);
            this.context.forMidiEffect(parentDevice);
        } else {
            this.context.forMidiEffect(this);
        }
        this.parameters.forEach(
            parameter =>
                parameter.setContext(this.context));
    }

    getText() {
        let params = this.getVisualizedParameters().map(
            x => x.get(true))
            .map(x => typeof x === "number" ? Math.round(100*x)/100 : x)
            .map(x => x > 0 ? "+" + x : x);
        return params.join(", ");
    }

    getVisualizedParameters() {
        return this.parameters.filter(
            x => x.isMapped);
    }

    isRealTime() {
        return true;
    }

    getEffectType() {
        return MIDI_FX;
    } 

    isMidiEffect() {
        return true;
    }

    needsStableLookAhead() {
        return false;
    }
    
    onNewStep() {
        this.clear();
    }

    stop() {}
    
    clear() {
        this.triggers = [];
    }

    input(trigger) {
        trigger.outputNumber = 0;
        this.triggers.push(trigger);
    }

    // must be overridden by sub-classes
    output() {}

    removeNote() {}

    getCopy(track) {
        return this;
    }

    addNote(transpose, sliceNumber=0, stepNumber=0) {
        let stepData = this.getStep(this.track.trackData, transpose, null, false);
        stepData.velocity = this.track.trackData.velocity;
        stepData.duration = null;
        stepData.transpose = transpose;
        stepData.slice = sliceNumber;

        this.input(new MidiTrigger(stepData, 0, stepNumber), true);
    }

    getStep(step, transpose=step.transpose, duration=step.durationParameter.get(), useCache=true) {
        let stepToUse = undefined;
        if (useCache) {
            stepToUse = this.cachedSteps[this.cacheIndex];
            this.cacheIndex = (this.cacheIndex + 1) % this.cachedSteps.length;
        } else {
            stepToUse = new StepData(undefined, undefined, undefined, this.track);
        }
        stepToUse.transpose = transpose;
        stepToUse.velocity = step.velocity;
        stepToUse.on = step.on;
        stepToUse.duration = duration;
        stepToUse.attack = step.attack;
        if (step.track) {
            stepToUse.track = step.track;
        }
        if (step.track ? step.track.instrument : this.track.instrument) {
            stepToUse.release = null; 
        } else {
            stepToUse.release = step.release;
        }
        stepToUse.start = step.start;
        stepToUse.chop = step.chop;
        stepToUse.pitchAttack = step.pitchAttack;
        stepToUse.pitchSustain = step.pitchSustain;
        stepToUse.pitchRelease = step.pitchRelease;
        stepToUse.pitchDecay = step.pitchDecay;
        stepToUse.slice = step.slice;
        stepToUse.reverse = step.reverse;
        stepToUse.panning = step.panning;
        stepToUse.selectedRecording = step.selectedRecording;
        stepToUse.palette = step.palette;
        stepToUse.auxEventA = step.auxEventA;
        stepToUse.auxEventB = step.auxEventB;
        stepToUse.auxEventC = step.auxEventC;
        stepToUse.auxEventD = step.auxEventD;
        return stepToUse;
    }

    initCache(cacheSize=this.cacheSize) {
        this.cachedSteps = [];

        for (let i=0; i < cacheSize; i++) {
            let stepData = new StepData(undefined, undefined, undefined, this.track);
            stepData.loadDefaults();
            this.cachedSteps.push(stepData);
        }

        this.cacheIndex = 0;
    }

    copyTrigger(trigger, useCache=false) {
        let duration = trigger.stepData.duration;
        let _stepData = trigger.stepData || new StepData();
        if (this.track && !this._subSequence) {
            _stepData.track = this.track;
        }
        let stepData = this.getStep(_stepData, undefined, undefined, useCache);
        let t = new MidiTrigger(stepData, trigger.time, trigger.stepNumber, trigger.outputNumber);
        if (trigger.grabs) {
            t.grabs = [... trigger.grabs];
        }
        if (trigger.chokes) {
            t.chokes = [... trigger.chokes];
        }
        if (trigger.message) {
            t.message = {... trigger.message};
        }
        return t;
    }

    dispose() {
        // we need to remove this from pulled effects if it exists
        this.name = undefined;
        let pulledEffects = this.track.sequencerEngine.state.effects.pulledEffects;
        this.track.sequencerEngine.dispatch(
            setPulledEffects(pulledEffects.filter(e => e !== this)));
    }

    getParameters() {
        return this.parameters;
    }

    isRolling() {
        return this.track.sequencerEngine.isSequenceRolling;
    }

}

export default MidiEffect;
